import 'rxjs/add/observable/empty';
import omit from 'lodash/omit';
import without from 'lodash/without';
import { Observable } from 'rxjs/Observable';
import type { Subscription } from 'rxjs/Subscription';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { NEW_GROUP_ID } from '../../../../common/constants';
import type { Group } from '../../../../common/types';
import removeGroupRest from '../../../remove-group';
import type { ImmutableState, ContainerProps } from '../../types';
import type { ActionCallback } from '../types';
import { removeGroupMutator, addRevertFlagToErrorResponse } from '../utils';

const getRemovedState = (groupId: number, state: ImmutableState): ImmutableState => {
	const { ticketTypesByGroup, groupsByTicketType } = state;
	const { [groupId.toString()]: ticketTypesForRemovedGroup = [], ...ticketTypesForOtherGroups } =
		ticketTypesByGroup;

	const updatedGroupsByTicketType =
		groupId === NEW_GROUP_ID
			? groupsByTicketType
			: ticketTypesForRemovedGroup.reduce((acc, ticketTypeId: number) => {
					const groups = acc[ticketTypeId.toString()];

					if (groups) {
						return groups.length <= 1
							? omit(acc, ticketTypeId.toString())
							: {
									// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
									...acc,
									[ticketTypeId]: without(groups, groupId),
								};
					}

					return acc;
				}, groupsByTicketType);

	return {
		...removeGroupMutator(groupId, state),
		ticketTypesByGroup: ticketTypesForOtherGroups,
		groupsByTicketType: updatedGroupsByTicketType,
	};
};

const removeGroup =
	(groupId: number, callback: ActionCallback<Group>) =>
	(
		{ setState, getState }: StoreActionApi<ImmutableState>,
		{ projectId, activeStreams }: ContainerProps,
	): Subscription => {
		if (NEW_GROUP_ID === groupId) {
			setState(getRemovedState(groupId, getState()));
			return Observable.empty<never>().subscribe();
		}

		const subscription = removeGroupRest(projectId, groupId).subscribe(
			(group) => {
				setState(getRemovedState(groupId, getState()));
				callback(group);
			},
			(error: ErrorResponse) => {
				// Since we are using visuals to hide the group while remove is pending
				// We should be always able to revert the state
				callback(undefined, addRevertFlagToErrorResponse(error, true));
			},
		);

		activeStreams && activeStreams.add(subscription);

		return subscription;
	};

export default removeGroup;
